import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Language,
  Public,
  AttachMoney,
  Save,
  LocationOn,
  Edit,
  Close,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { SupportedLanguage, SupportedRegion } from '../i18n';
import { US_STATES } from '../utils/usStates';

interface LocaleSettingsCardProps {
  onSave?: () => void;
}

export const LocaleSettingsCard: React.FC<LocaleSettingsCardProps> = ({ onSave }) => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const queryClient = useQueryClient();
  const { refreshUserProfile } = useAuth();
  const {
    language,
    region,
    setLanguage,
    setRegion,
    supportedLanguages,
    supportedRegions,
  } = useLocale();

  const [editing, setEditing] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(language);
  const [selectedRegion, setSelectedRegion] = useState<SupportedRegion>(region);
  const [selectedState, setSelectedState] = useState<string>('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { data: savedLocale, isLoading: localeLoading } = useQuery({
    queryKey: ['locale-preferences'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/user/preferences/locale', { headers });
      if (!res.ok) throw new Error('Failed to load preferences');
      const json = await res.json();
      return json.data as { language?: string; region?: string; currency?: string; state?: string } | null;
    },
  });

  useEffect(() => {
    if (savedLocale && !initialized) {
      if (savedLocale.language) setSelectedLanguage(savedLocale.language as SupportedLanguage);
      if (savedLocale.region) setSelectedRegion(savedLocale.region as SupportedRegion);
      if (savedLocale.state) setSelectedState(savedLocale.state);
      setInitialized(true);
    }
  }, [savedLocale, initialized]);

  const savedLang = (savedLocale?.language || language) as SupportedLanguage;
  const savedReg = (savedLocale?.region || region) as SupportedRegion;
  const savedState = savedLocale?.state || '';

  const savePreferencesMutation = useMutation({
    mutationFn: async (preferences: {
      language: SupportedLanguage;
      region: SupportedRegion;
      state?: string;
    }) => {
      const headers = await getAuthHeaders();
      const payload: { language: string; region: string; currency: string; state?: string } = {
        language: preferences.language,
        region: preferences.region,
        currency: supportedRegions[preferences.region].currency,
      };
      if (preferences.region === 'US' && preferences.state) {
        payload.state = preferences.state;
      }
      const response = await makeApiCall('/user/preferences/locale', {
        method: 'PUT',
        headers,
        body: JSON.stringify({ locale: payload }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save preferences');
      }
      
      return response.json();
    },
    onSuccess: () => {
      setLanguage(selectedLanguage);
      setRegion(selectedRegion);
      refreshUserProfile();
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['locale-preferences'] });
      setSaveSuccess(true);
      setEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
      onSave?.();
    },
  });

  const handleSave = () => {
    savePreferencesMutation.mutate({
      language: selectedLanguage,
      region: selectedRegion,
      state: selectedRegion === 'US' ? selectedState || undefined : undefined,
    });
  };

  const handleCancel = () => {
    setSelectedLanguage(savedLang);
    setSelectedRegion(savedReg);
    setSelectedState(savedState);
    setEditing(false);
  };

  const displayCurrency = supportedRegions[savedReg]?.currency || 'USD';
  const displayLangCode = savedLang;
  const displayLangInfo = supportedLanguages[displayLangCode];
  const displayRegionName = savedReg === 'US' ? 'United States (US)' : 'Canada (CA)';
  const displayStateName = savedState ? (US_STATES as Record<string, string>)[savedState] : '';

  const ViewRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <Language sx={{ color: 'primary.main' }} />
            {t('settings.language')} & {t('settings.region')}
          </Typography>
          {!editing && !localeLoading && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => setEditing(true)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {editing && (
            <Tooltip title="Cancel">
              <IconButton size="small" onClick={handleCancel}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }}>
            {t('messages.saveSuccess')}
          </Alert>
        )}

        {savePreferencesMutation.isError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
            {savePreferencesMutation.error instanceof Error 
              ? savePreferencesMutation.error.message 
              : t('errors.generic')}
          </Alert>
        )}

        {localeLoading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
          </Stack>
        ) : !editing ? (
          <Stack spacing={0} sx={{ py: 0.5 }}>
            <ViewRow
              icon={<Language fontSize="small" />}
              label={t('settings.language')}
              value={displayLangInfo ? `${displayLangInfo.nativeName} (${displayLangInfo.name})` : displayLangCode}
            />
            <ViewRow
              icon={<Public fontSize="small" />}
              label={t('settings.region')}
              value={displayRegionName}
            />
            {savedReg === 'US' && displayStateName && (
              <ViewRow
                icon={<LocationOn fontSize="small" />}
                label={t('settings.state')}
                value={`${displayStateName} (${savedState})`}
              />
            )}
            <ViewRow
              icon={<AttachMoney fontSize="small" />}
              label={t('settings.currency')}
              value={`${displayCurrency} (${displayCurrency === 'USD' ? 'US Dollar' : 'Canadian Dollar'})`}
            />
          </Stack>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="language-select-label">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Language fontSize="small" />
                  {t('settings.language')}
                </Box>
              </InputLabel>
              <Select
                labelId="language-select-label"
                value={selectedLanguage}
                label={t('settings.language')}
                onChange={(e) => setSelectedLanguage(e.target.value as SupportedLanguage)}
              >
                {Object.entries(supportedLanguages)
                  .filter(([code]) => selectedRegion !== 'US' || code !== 'fr')
                  .map(([code, { name, nativeName }]) => (
                    <MenuItem key={code} value={code}>
                      {nativeName} ({name})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="region-select-label">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Public fontSize="small" />
                  {t('settings.region')}
                </Box>
              </InputLabel>
              <Select
                labelId="region-select-label"
                value={selectedRegion}
                label={t('settings.region')}
                onChange={(e) => {
                  const val = e.target.value as SupportedRegion;
                  setSelectedRegion(val);
                  if (val !== 'US') setSelectedState('');
                  // French is only supported in the Canadian market
                  if (val === 'US' && selectedLanguage === 'fr') setSelectedLanguage('en');
                }}
              >
                {Object.entries(supportedRegions).map(([code]) => (
                  <MenuItem key={code} value={code}>
                    {code === 'US' ? t('pricing.regionUS') : t('pricing.regionCA')} ({code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedRegion === 'US' && (
              <FormControl fullWidth size="small">
                <InputLabel id="state-select-label">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" />
                    {t('settings.state')}
                  </Box>
                </InputLabel>
                <Select
                  labelId="state-select-label"
                  value={selectedState}
                  label={t('settings.state')}
                  onChange={(e) => setSelectedState(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{t('settings.stateOptional')}</em>
                  </MenuItem>
                  {Object.entries(US_STATES).map(([code, name]) => (
                    <MenuItem key={code} value={code}>
                      {name} ({code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}>
              <AttachMoney fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('settings.currency')}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {supportedRegions[selectedRegion]?.currency || 'USD'} ({(supportedRegions[selectedRegion]?.currency || 'USD') === 'USD' ? 'US Dollar' : 'Canadian Dollar'})
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleCancel}
                disabled={savePreferencesMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={savePreferencesMutation.isPending ? <CircularProgress size={16} /> : <Save />}
                onClick={handleSave}
                disabled={savePreferencesMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default LocaleSettingsCard;
